import { dataObjectApi } from './dataObjectApi';

export type WorkspacePermission = 'VIEW' | 'EDIT' | 'MANAGE';

export type WorkspaceFolder = {
  id: string;
  name: string;
  parentId?: string | null;
  /** NULL for company-space folders; owning username for personal roots. */
  ownerUsername?: string | null;
  createdBy?: string | null;
  /** Caller's effective permission on the folder. */
  permission?: WorkspacePermission | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkspaceCaller = {
  username: string | null;
  admin: boolean;
  editor: boolean;
  companyDefaultPermission: WorkspacePermission;
};

export type WorkspaceState = {
  folders: WorkspaceFolder[];
  sharedFolderIds: string[];
  assignments: Record<string, string>;
  itemPermissions: Record<string, WorkspacePermission>;
  hiddenItems: string[];
  sharedItems: string[];
  folderTags: Record<string, string[]>;
  itemTags: Record<string, string[]>;
  personalRootFolderId: string | null;
  caller: WorkspaceCaller | null;
};

export type WorkspaceAssignment = {
  itemKey: string;
  folderId: string | null;
};

export type WorkspaceResourceType = 'FOLDER' | 'ITEM';

export type WorkspaceAclEntry = {
  principalType: 'USER' | 'GROUP' | 'EVERYONE';
  principalId: string;
  principalName?: string | null;
  permission: WorkspacePermission;
  createdBy?: string | null;
};

export type WorkspaceAclState = {
  entries: WorkspaceAclEntry[];
  effectivePermission: WorkspacePermission;
  sourceType: 'ITEM' | 'FOLDER' | 'PERSONAL_DEFAULT' | 'COMPANY_DEFAULT';
  sourceFolderId?: string | null;
  sourceFolderName?: string | null;
  canManage: boolean;
};

export type WorkspaceGroup = {
  id: string;
  name: string;
  description?: string | null;
  createdBy: string;
  members: string[];
  canManage: boolean;
  createdAt: string;
  updatedAt: string;
};

const aclParams = (resourceType: WorkspaceResourceType, resourceKey: string) => ({
  params: { resourceType, resourceKey },
});

export const workspaceApi = {
  getState: async (): Promise<WorkspaceState> => {
    return dataObjectApi.get<WorkspaceState>('/api/v1/workspaces');
  },

  ensurePersonalRoot: async (): Promise<WorkspaceFolder> => {
    return dataObjectApi.post<WorkspaceFolder>('/api/v1/workspaces/personal-root', {});
  },

  createFolder: async (payload: {
    name: string;
    parentId?: string | null;
    personal?: boolean;
  }): Promise<WorkspaceFolder> => {
    return dataObjectApi.post<WorkspaceFolder>('/api/v1/workspaces/folders', payload);
  },

  deleteFolder: async (folderId: string): Promise<void> => {
    await dataObjectApi.delete(`/api/v1/workspaces/folders/${folderId}`);
  },

  assignItem: async (
    itemKey: string,
    folderId: string | null
  ): Promise<WorkspaceAssignment> => {
    return dataObjectApi.put<WorkspaceAssignment>(
      `/api/v1/workspaces/items/${encodeURIComponent(itemKey)}`,
      { folderId }
    );
  },

  getAcl: async (
    resourceType: WorkspaceResourceType,
    resourceKey: string
  ): Promise<WorkspaceAclState> => {
    return dataObjectApi.get<WorkspaceAclState>(
      '/api/v1/workspaces/acl',
      aclParams(resourceType, resourceKey)
    );
  },

  setAcl: async (
    resourceType: WorkspaceResourceType,
    resourceKey: string,
    entries: Pick<WorkspaceAclEntry, 'principalType' | 'principalId' | 'permission'>[]
  ): Promise<WorkspaceAclState> => {
    return dataObjectApi.put<WorkspaceAclState>(
      '/api/v1/workspaces/acl',
      { entries },
      aclParams(resourceType, resourceKey)
    );
  },

  setTags: async (
    resourceType: WorkspaceResourceType,
    resourceKey: string,
    tags: string[]
  ): Promise<{ tags: string[] }> => {
    return dataObjectApi.put<{ tags: string[] }>(
      '/api/v1/workspaces/tags',
      { tags },
      aclParams(resourceType, resourceKey)
    );
  },

  listGroups: async (): Promise<WorkspaceGroup[]> => {
    return dataObjectApi.get<WorkspaceGroup[]>('/api/v1/workspaces/groups');
  },

  createGroup: async (payload: {
    name: string;
    description?: string;
    members?: string[];
  }): Promise<WorkspaceGroup> => {
    return dataObjectApi.post<WorkspaceGroup>('/api/v1/workspaces/groups', payload);
  },

  updateGroup: async (
    groupId: string,
    payload: { name?: string; description?: string; members?: string[] }
  ): Promise<WorkspaceGroup> => {
    return dataObjectApi.put<WorkspaceGroup>(
      `/api/v1/workspaces/groups/${groupId}`,
      payload
    );
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await dataObjectApi.delete(`/api/v1/workspaces/groups/${groupId}`);
  },
};
